import { useEffect, useMemo, useState } from 'react';
import type { DateRange } from '@library/types/dates';
import { DateTime } from 'luxon';
import type { LeagueInfo } from '@library/types/leagues';
import { getMainDuration } from '@bo-utils/leagueAttributes';
import { isTimeAfter } from '@library/utils/dateTime';
import { uniqueLeagueDurations } from '@bo-utils/leagueProjectionFilters';

export function useGlobalFilters({
  initialStartDate = DateTime.now(),
  initialEndDate = DateTime.now(),
  leagueInfo
}: {
  initialStartDate?: DateTime;
  initialEndDate?: DateTime;
  leagueInfo: LeagueInfo | undefined;
}) {
  const [searchValue, setSearchValue] = useState('');
  const [selectedStartDate, setSelectedStartDate] =
    useState<DateTime>(initialStartDate);
  const [selectedEndDate, setSelectedEndDate] =
    useState<DateTime>(initialEndDate);

  const [selectedDurations, setSelectedDurations] = useState<Array<string>>([]);

  const handleMultiSelectDurations = (value: Array<string>) =>
    setSelectedDurations(value);

  const durations = useMemo(
    () => uniqueLeagueDurations(leagueInfo),
    [leagueInfo]
  );

  useEffect(() => {
    if (!leagueInfo || !durations) {
      handleMultiSelectDurations([]);
      return;
    }

    const initialDuration = getMainDuration({
      durations,
      leagueName: leagueInfo.name
    });

    const leagueProjectionFilterId = initialDuration
      ? initialDuration.leagueProjectionFilterId
      : durations[0].leagueProjectionFilterId;

    handleMultiSelectDurations(
      leagueProjectionFilterId ? [leagueProjectionFilterId] : []
    );
  }, [durations, leagueInfo]);

  const handleSelectDateRange = (value: DateRange) => {
    const { fromDate: newFromDate, toDate: newToDate } = value;

    const newSelectedEndDate =
      newToDate &&
      isTimeAfter({ dateValue: newFromDate, dateToCompareAgainst: newToDate })
        ? newFromDate
        : newToDate;
    setSelectedStartDate(newFromDate);
    setSelectedEndDate(newSelectedEndDate);
  };

  const fromDate = selectedStartDate.startOf('day').toISODate() || '';
  const toDate = selectedEndDate.endOf('day').toISODate() || undefined;

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const initialDurationId = useMemo(() => {
    if (!leagueInfo || !durations) {
      return undefined;
    }

    const initialDuration = getMainDuration({
      durations,
      leagueName: leagueInfo.name
    });

    return initialDuration
      ? initialDuration.leagueProjectionFilterId
      : durations[0].leagueProjectionFilterId;
  }, [leagueInfo, durations]);

  return {
    durations,
    fromDate,
    handleMultiSelectDurations,
    handleSearch,
    handleSelectDateRange,
    searchValue,
    selectedDuration: initialDurationId,
    selectedDurations,
    selectedEndDate,
    selectedStartDate,
    toDate
  };
}
